'use client';

// imports
import { useEffect, useState } from 'react';

//components
import ITSImage from '@/components/ITSImage/ITSImage';
import LinkButton from '@/components/Button/LinkButton';

// lib files
import { StationData } from "@/lib/types/api/stations-data";

// styles
import styles from './PBSAppSearchRow.module.scss';

interface PBSAppSearchRowProps {
  depDevicesImageUrl?: string;
  stationData?: StationData;
}

interface PBSDeviceImagesProps {
  depDevicesImageUrl?: string;
  stationId?: string;
}

// The fallback image does not have the most up-to-date shows, so we only use it if we don't have a CS image
const FALLBACK_IMAGE = "https://image.pbs.org/webobjects/pbs-devices-in-use.png";

const fetchDevicesImageUrl = async (stationId?: string) => {
  const query = stationId ? `?stationId=${encodeURIComponent(stationId)}` : '';
  const response = await fetch(`/api/pbs-app-devices-image/${query}`);

  if(!response.ok) {
    throw new Error(`Failed with status ${response.status}`);
  }

  const json = await response.json();
  return json.devicesImageUrl;
}

// A client-side twin of PBSAppDevicesImage. That component is async because it reads
// CS directly. Since this has to be client-side (it appears on the search results page which is entirely 'use client') it makes a call to an internal API route.
const PBSDeviceImage = (props: PBSDeviceImagesProps) => {
  const { depDevicesImageUrl, stationId } = props;
  const [devicesImageUrl, setDevicesImageUrl] = useState(depDevicesImageUrl);

  useEffect(() => {
    if(depDevicesImageUrl) {
      return
    }

    fetchDevicesImageUrl(stationId)
      .then(url => setDevicesImageUrl(url || FALLBACK_IMAGE))
      .catch(() => setDevicesImageUrl(FALLBACK_IMAGE))
  // deliberately only running this after the initial render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if(!devicesImageUrl) {
    return null;
  }

  const deviceImageSizes = `
    (min-width: 1700px) 697px,
    (min-width: 1440px) 38vw,
    (min-width: 1024px) 32vw,
    (min-width: 768px) 30vw,
    calc(100vw - 64px)
  `

  return (
    <ITSImage
      src={devicesImageUrl}
      alt={"PBS content being displayed on a phone, TV, and tablet."}
      width={703}
      height={303}
      resizeWithCrop={true}
      ariaHidden={true}
      sizes={deviceImageSizes}
      srcSetSizes={[[256, 111], [336, 145], [536, 231], [703, 303], [1000, 432], [1200, 518], [1406, 606]]}
      className={styles.pbs_app_search_row__devices_image}
    />
  );
};

const PBSAppSearchRow = (props: PBSAppSearchRowProps) => {
  const { depDevicesImageUrl, stationData } = props;
  const {  id  } = stationData || {};

  return (
    <div className={styles.pbs_app_search_row}>

      <div className={styles.pbs_app_search_row__content}>
        <h2 className={styles.pbs_app_search_row__title}>
          <a
            href="https://www.pbs.org/pbs-app/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Stream on the PBS App Anywhere You Watch
          </a>
        </h2>
        <p className={styles.pbs_app_search_row__description}>Watch your favorite PBS shows and enjoy live TV from your local station anytime on the PBS app, all from your favorite device.</p>
        <LinkButton
          href="https://www.pbs.org/pbs-app/"
          target="_blank"
          style="white"
          className={styles.pbs_app_search_row__button}
        >
          Download the App
        </LinkButton>
      </div>
      <a
        href="https://www.pbs.org/pbs-app/"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.pbs_app_search_row__devices_image_link}
        // hiding this from assistive tech because it's a redundant link
        aria-hidden="true"
        tabIndex={-1}
      >
        <PBSDeviceImage
          depDevicesImageUrl={depDevicesImageUrl}
          stationId={id}
        />
      </a>
    </div>
  );
};

export default PBSAppSearchRow;
